/**
 * Sidebar Navigation Component
 * Collapsible sidebar with role-filtered navigation items
 */
import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { NAV_ITEMS } from '../../config/routes';
import {
  LayoutDashboard, ClipboardList, Calendar, UserCheck,
  GraduationCap, BookOpen, Layers, Award, Users, UserCog,
  Link as LinkIcon, PenTool, FileEdit, MessageSquare,
  Trophy, BarChart3, Settings, FileText, ChevronDown,
  ChevronLeft, ChevronRight, Search, Bell
} from 'lucide-react';

// Icon mapping
const iconMap = {
  LayoutDashboard, ClipboardList, Calendar, UserCheck,
  GraduationCap, BookOpen, Layers, Award, Users, UserCog,
  Link: LinkIcon, PenTool, FileEdit, MessageSquare,
  Trophy, BarChart3, Settings, FileText, Search, Bell
};

function getIcon(name, size = 18) {
  const Icon = iconMap[name];
  return Icon ? <Icon size={size} /> : null;
}

export default function Sidebar({ collapsed, onToggle, onItemClick }) {
  const { userProfile } = useAuth();
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState({});

  const userRole = userProfile?.role;

  // Filter nav items by role
  const filteredItems = NAV_ITEMS.filter(
    (item) => item.roles.includes(userRole)
  );

  const toggleGroup = (id) => {
    setExpandedGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const isActive = (path) => location.pathname === path;
  const isGroupActive = (item) => {
    if (item.children) {
      return item.children.some((child) => location.pathname.startsWith(child.path));
    }
    return false;
  };

  return (
    <aside
      className={`fixed left-0 top-16 bottom-0 z-40 bg-slate-900/95 backdrop-blur-xl border-r border-white/5 transition-all duration-300 flex flex-col ${
        collapsed ? 'w-[68px]' : 'w-64'
      }`}
    >
      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 scrollbar-thin">
        <ul className="space-y-1">
          {filteredItems.map((item) => {
            if (item.children) {
              // Group with children
              const isExpanded = expandedGroups[item.id] || isGroupActive(item);
              return (
                <li key={item.id}>
                  <button
                    onClick={() => !collapsed && toggleGroup(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isGroupActive(item)
                        ? 'text-teal-400 bg-teal-500/10'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
                    }`}
                    title={collapsed ? item.label : undefined}
                  >
                    <span className="flex-shrink-0">{getIcon(item.icon)}</span>
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left">{item.label}</span>
                        <ChevronDown
                          size={14}
                          className={`transition-transform duration-200 ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      </>
                    )}
                  </button>
                  {!collapsed && isExpanded && (
                    <ul className="mt-1 ml-4 pl-3 border-l border-white/5 space-y-0.5">
                      {item.children.map((child) => (
                        <li key={child.id}>
                          <NavLink
                            to={child.path}
                            onClick={onItemClick}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                              isActive(child.path)
                                ? 'text-teal-400 bg-teal-500/10 font-medium'
                                : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'
                            }`}
                          >
                            <span className="flex-shrink-0">{getIcon(child.icon, 16)}</span>
                            <span>{child.label}</span>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            }

            // Single nav item
            return (
              <li key={item.id}>
                <NavLink
                  to={item.path}
                  onClick={onItemClick}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive(item.path)
                      ? 'text-teal-400 bg-teal-500/10 shadow-lg shadow-teal-500/5'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
                  }`}
                  title={collapsed ? item.label : undefined}
                >
                  <span className="flex-shrink-0">{getIcon(item.icon)}</span>
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Collapse Toggle */}
      <div className="p-3 border-t border-white/5">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-all duration-200 text-sm"
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronRight size={16} /> : (
            <>
              <ChevronLeft size={16} />
              <span>Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
